import { use } from 'react';

import { Agent } from '#plugin-chat/components/agent/Agent.tsx';
import { AtlasInstructions } from '#plugin-chat/components/agent/AtlasInstructions.tsx';
import { AssistantContext } from '#plugin-chat/contexts/AssistantContext.tsx';
import { InstructionContext } from '#plugin-chat/contexts/InstructionContext.ts';
import { InstructionProvider } from '#plugin-chat/contexts/InstructionProvider.tsx';

interface AtlasAssistantProps {
  children?: React.ReactNode;
  name?: string;
  description?: string;
}

/**
 * Inner component that uses instruction context to build system prompt.
 * Separated from outer component to access context after InstructionProvider wraps it.
 */
function AtlasAssistantInner({
  children,
  name: assistantName = 'Cognite Atlas AI™',
  description = 'An AI assistant specialized in querying and analyzing data in Cognite Data Fusion.',
}: AtlasAssistantProps) {
  // Get system instructions from context (collected from Instructions + tool instructions)
  const { getInstructions } = use(InstructionContext);
  const systemInstructions = getInstructions('system')
    .map((b) => b.content)
    .join('\n\n');

  console.debug('System instructions for assistant:', systemInstructions);

  return (
    <AssistantContext
      value={{
        name: assistantName,
        description,
        system: systemInstructions,
        agents: {},
        activeAgent: assistantName,
        registerAgent: (_agentName: string, _props) => () => {},
      }}
    >
      <Agent
        abortTaskTool={null}
        // callSettings={{ temperature: 0.2 }}
        completeTaskTool={null}
        description={description}
        name={assistantName}
        system={systemInstructions}
      >
        {children}
      </Agent>
    </AssistantContext>
  );
}

/**
 * Orchestrates multiple AI agents with task delegation capabilities.
 * Child Agent components are registered dynamically and exposed via submit_task_to_agent tool.
 *
 * Wraps component tree with InstructionProvider to enable tools to inject instructions.
 * Instructions are registered inline using the Instructions component.
 */
export function AtlasAssistant(props: AtlasAssistantProps) {
  const { name: assistantName = 'Cognite Atlas AI™' } = props;

  return (
    <InstructionProvider>
      <AtlasInstructions name={assistantName} />
      <AtlasAssistantInner {...props} />
    </InstructionProvider>
  );
}
